import React, { useEffect, useMemo, useState } from "react";
import { useVenue } from "../VenueProvider";
import { useAuth } from "../AuthProvider";
import { showAlert } from "../../alertService";
import { logBitacora, BITACORA_ACTIONS } from "../../utils/bitacora";
import {
  incluidosSinRenta,
  incluyeDe,
  pendientesDeClasificar,
  precioDe,
  rentasDe,
  unidadDe,
} from "../../utils/catalogo";
import {
  asignarIncluido,
  formDesdeServicio,
  validarForm,
  guardarServicio,
} from "../../utils/servicios";
import ServicioForm from "./ServicioForm";
import ServicioCreateDialog from "./ServicioCreateDialog";
import { XMarkIcon, ArrowRightIcon, CheckCircleIcon } from "@heroicons/react/20/solid";

const ACCENT = "#1b4d2e";

// Asistente del catálogo. Dos pasadas, en este orden:
//
//   1. CLASIFICAR. Recorre los servicios que nadie ha revisado (los conceptos
//      de siempre, solo nombre y precio) y les hace la misma pregunta del alta
//      —¿qué es?— con el MISMO formulario (FRA-348). Aquí es donde la "Renta
//      de salón" que el venue ya tenía cargada se vuelve una renta de verdad,
//      con lo que trae dentro.
//
//   2. ACOMODAR LOS INCLUIDOS SUELTOS. Los servicios marcados "incluido en la
//      renta" que no viven en ninguna renta. Antes de que la renta existiera
//      como tipo, esa casilla era el único lugar donde decir "esto no se cobra
//      solo", así que la limpieza de baños quedaba existiendo sin que nada la
//      ligara al salón —y por eso no aparecía en ninguna cotización—. Esta
//      pasada pregunta en qué rentas va y lo escribe en la lista de ellas.
//      No corre si el venue todavía no tiene ninguna renta: primero hay padre.
//
// Un servicio sin clasificar sigue funcionando en cotizaciones exactamente
// como siempre; esto solo le agrega lo que le falta.
//
// Se guarda renglón por renglón, no al final: el owner puede salirse en el 5
// de 14 y retomar sin perder nada.
const ClasificacionWizard = ({ open, onClose }) => {
  const { selectedVenueId, conceptos, proveedores } = useVenue();
  const { userInfo, user } = useAuth();
  const [saving, setSaving] = useState(false);
  const [hechos, setHechos] = useState([]);
  const [saltados, setSaltados] = useState([]);
  // Segunda pasada: acomodar los incluidos sueltos.
  const [acomodados, setAcomodados] = useState([]);
  const [saltadosAcomodo, setSaltadosAcomodo] = useState([]);
  const [rentasElegidas, setRentasElegidas] = useState([]);
  const [showCrearRenta, setShowCrearRenta] = useState(false);

  const pendientes = useMemo(
    () => pendientesDeClasificar(conceptos).filter((c) => !saltados.includes(c.id)),
    [conceptos, saltados]
  );
  const actual = pendientes[0] || null;
  const total = pendientes.length + hechos.length;

  const [form, setForm] = useState(() => formDesdeServicio(actual));
  useEffect(() => {
    setForm(formDesdeServicio(actual));
  }, [actual?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // El componente vive montado aunque esté cerrado: al reabrirlo se empieza
  // de cero, si no el contador y los saltados sobreviven a la sesión anterior.
  useEffect(() => {
    if (open) {
      setHechos([]);
      setSaltados([]);
      setAcomodados([]);
      setSaltadosAcomodo([]);
    }
  }, [open]);

  // ── Pasada 2: los incluidos que no viven en ninguna renta ──
  const rentas = useMemo(() => rentasDe(conceptos), [conceptos]);
  const sueltos = useMemo(
    () =>
      incluidosSinRenta(conceptos).filter(
        (c) => !saltadosAcomodo.includes(c.id) && !acomodados.includes(c.id)
      ),
    [conceptos, saltadosAcomodo, acomodados]
  );
  // Solo entra cuando ya no queda nada por clasificar: preguntar "¿en qué
  // renta va?" antes de que las rentas existan no tiene respuesta posible.
  const enAcomodo = !actual && rentas.length > 0 && sueltos.length > 0;
  const sueltoActual = enAcomodo ? sueltos[0] : null;

  // Al cambiar de servicio se pre-marcan las rentas que ya lo tienen (ninguna,
  // por definición de "suelto"; queda escrito así para que editar desde aquí
  // más adelante siga siendo correcto).
  useEffect(() => {
    if (!sueltoActual) return;
    setRentasElegidas(
      rentas.filter((r) => incluyeDe(r).some((i) => i.conceptoId === sueltoActual.id)).map((r) => r.id)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sueltoActual?.id]);

  const guardarAcomodo = async () => {
    if (!sueltoActual || !selectedVenueId) return;
    setSaving(true);
    try {
      await asignarIncluido({
        venueId: selectedVenueId,
        conceptoId: sueltoActual.id,
        rentas,
        rentaIdsElegidas: rentasElegidas,
        user,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_INCLUIDO_SET,
          entity: "servicio",
          entityId: sueltoActual.id,
          entityName: sueltoActual.nombre || "",
          details: { rentas: rentasElegidas.length, desdeAsistente: true },
        },
        userInfo,
        user
      );
      setAcomodados((a) => [...a, sueltoActual.id]);
    } catch (err) {
      showAlert(`No se pudo guardar. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const proveedoresPorId = useMemo(() => {
    const map = {};
    (proveedores || []).forEach((p) => { map[p.id] = p; });
    return map;
  }, [proveedores]);

  const conceptosPorId = useMemo(() => {
    const map = {};
    (conceptos || []).forEach((c) => { if (c?.id) map[c.id] = c; });
    return map;
  }, [conceptos]);

  const guardar = async (formAGuardar = form) => {
    if (!actual || !selectedVenueId) return;
    const error = validarForm(formAGuardar, { conceptosPorId, itemId: actual.id });
    if (error) {
      showAlert(error, "error");
      return;
    }
    setSaving(true);
    try {
      await guardarServicio({
        venueId: selectedVenueId,
        itemId: actual.id,
        actual,
        form: formAGuardar,
        user,
        userInfo,
        proveedoresPorId,
        conceptosPorId,
      });
      logBitacora(
        selectedVenueId,
        {
          action: BITACORA_ACTIONS.SERVICIO_CLASIFICAR,
          entity: "servicio",
          entityId: actual.id,
          entityName: actual.nombre || "",
          details: {
            proveedorId: formAGuardar.deProveedor ? formAGuardar.proveedorId : null,
            itemizado: !!formAGuardar.itemizado,
          },
        },
        userInfo,
        user
      );
      setHechos((h) => [...h, actual.id]);
    } catch (err) {
      showAlert(`No se pudo guardar. [${err.message}]`, "error");
    } finally {
      setSaving(false);
    }
  };

  const saltar = () => {
    if (!actual) return;
    setSaltados((s) => [...s, actual.id]);
  };

  if (!open) return null;

  const listo = !actual && !enAcomodo;
  const progreso = total > 0 ? hechos.length / total : 1;
  const totalAcomodo = acomodados.length + sueltos.length;
  const progresoAcomodo = totalAcomodo > 0 ? acomodados.length / totalAcomodo : 1;
  // Hay incluidos sueltos pero ninguna renta donde meterlos: no es un paso que
  // se pueda contestar, es un aviso con su salida.
  const sueltosTotales = incluidosSinRenta(conceptos).length;
  const faltaLaRenta = !actual && rentas.length === 0 && sueltosTotales > 0;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-2xl rounded-xl border border-zinc-200 bg-white shadow-2xl dark:border-zinc-700/60 dark:bg-zinc-900 flex flex-col max-h-[92vh]">
        <div className="px-5 py-4 border-b border-zinc-200 dark:border-zinc-700/60">
          <div className="flex items-center gap-3">
            <h2 className="text-base font-semibold text-zinc-900 dark:text-white">
              Revisar el catálogo
            </h2>
            {(enAcomodo ? totalAcomodo : total) > 0 && (
              <span className="ml-auto text-xs text-zinc-400">
                {enAcomodo
                  ? `${acomodados.length} de ${totalAcomodo}`
                  : `${hechos.length} de ${total}`}
              </span>
            )}
            <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-600">
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          {/* La barra aparece cuando hay algo que medir: al 0 % era una
              raya gris sin significado debajo del título, y el contador de
              arriba ya dice dónde vas. */}
          {(enAcomodo ? acomodados.length > 0 : hechos.length > 0) && (
            <div className="mt-2 h-1.5 w-full rounded-full bg-zinc-100 dark:bg-zinc-700/60 overflow-hidden">
              <div
                className="h-full rounded-full transition-all"
                style={{
                  width: `${Math.round((enAcomodo ? progresoAcomodo : progreso) * 100)}%`,
                  backgroundColor: ACCENT,
                }}
              />
            </div>
          )}
          {enAcomodo && (
            <p className="mt-2 text-xs text-zinc-500">
              Paso 2: en qué rentas va lo que no se cobra por separado.
            </p>
          )}
          {listo && faltaLaRenta && (
            <p className="mt-2 text-xs text-zinc-500">
              {sueltosTotales}{" "}
              {sueltosTotales === 1 ? "servicio incluido" : "servicios incluidos"} sin una
              renta que los traiga.
            </p>
          )}
        </div>

        {listo && faltaLaRenta ? (
          /* No es un final feliz con una nota al pie, y tampoco una página de
             error centrada: es un paso más del asistente, así que lleva el
             mismo ritmo que los otros —cuerpo con la lista, y la acción en la
             barra de abajo— en vez del icono grande con el botón suelto. */
          <>
            <div className="p-5 overflow-y-auto space-y-4">
              <p className="text-sm text-zinc-600 dark:text-zinc-300">
                {sueltosTotales === 1 ? "Este servicio no se cobra" : "Estos servicios no se cobran"}{" "}
                por separado, así que solo pueden entrar a una cotización si una renta
                los trae. No tienes ninguna renta dada de alta, así que hoy no aparecen
                en ninguna.
              </p>

              <ul className="rounded-lg border border-zinc-200 dark:border-zinc-700/60 divide-y divide-zinc-100 dark:divide-zinc-700/40">
                {incluidosSinRenta(conceptos).map((c) => (
                  <li key={c.id} className="flex items-center gap-3 px-4 py-2.5">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-zinc-900 dark:text-white truncate">
                        {c.nombre}
                      </div>
                      {c.descripcion && (
                        <div className="text-xs text-zinc-500 truncate">{c.descripcion}</div>
                      )}
                    </div>
                    <span className="text-xs text-zinc-400 whitespace-nowrap">
                      Sin renta
                    </span>
                  </li>
                ))}
              </ul>

              <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3">
                <div className="text-sm font-medium text-zinc-900 dark:text-white">
                  Da de alta la renta de tu espacio
                </div>
                <p className="mt-0.5 text-xs text-zinc-500">
                  Ponle nombre y precio —“Renta de salón”, “Renta de jardín”— y en el
                  mismo formulario eliges cuáles de estos van dentro. Si rentas por
                  días, cada día es su propia renta con su propia lista.
                </p>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={onClose}
                className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Más tarde
              </button>
              <button
                type="button"
                onClick={() => setShowCrearRenta(true)}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
                style={{ backgroundColor: ACCENT }}
              >
                Dar de alta la renta
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : listo ? (
          <div className="p-8 text-center">
            <CheckCircleIcon className="h-10 w-10 mx-auto text-emerald-500" />
            <div className="mt-3 text-lg font-semibold text-zinc-900 dark:text-white">
              {hechos.length > 0 || acomodados.length > 0
                ? "Catálogo revisado"
                : "No queda nada por revisar"}
            </div>
            <p className="mt-1 text-sm text-zinc-500">
              {saltados.length > 0 || saltadosAcomodo.length > 0
                ? `Saltaste ${saltados.length + saltadosAcomodo.length}. Puedes retomarlos cuando quieras desde el aviso.`
                : "Ahora cada servicio sabe qué es y quién lo pone, y lo que te cobran se registra solo al asignarlo a un evento."}
            </p>
            <button
              type="button"
              onClick={onClose}
              className="mt-4 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm"
              style={{ backgroundColor: ACCENT }}
            >
              Listo
            </button>
          </div>
        ) : enAcomodo ? (
          <>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3">
                <div className="text-lg font-semibold text-zinc-900 dark:text-white">
                  {sueltoActual.nombre}
                </div>
                <div className="text-sm text-zinc-500">
                  No se cobra por separado
                  {sueltoActual.descripcion ? ` · ${sueltoActual.descripcion}` : ""}
                </div>
              </div>

              <div>
                <div className="text-xs font-medium text-zinc-500 mb-2">
                  ¿En qué rentas va incluido? Puede ir en varias: el sábado del
                  evento y el viernes de montaje son rentas distintas.
                </div>
                <div className="space-y-2">
                  {rentas.map((r) => {
                    const marcada = rentasElegidas.includes(r.id);
                    return (
                      <label
                        key={r.id}
                        className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer transition ${
                          marcada
                            ? "border-transparent"
                            : "border-zinc-200 dark:border-zinc-700 hover:border-zinc-300"
                        }`}
                        style={
                          marcada
                            ? { backgroundColor: ACCENT + "0d", boxShadow: `0 0 0 2px ${ACCENT}` }
                            : undefined
                        }
                      >
                        <input
                          type="checkbox"
                          checked={marcada}
                          onChange={(e) =>
                            setRentasElegidas((prev) =>
                              e.target.checked
                                ? [...prev, r.id]
                                : prev.filter((x) => x !== r.id)
                            )
                          }
                          className="mt-1"
                        />
                        <span>
                          <span className="block text-sm font-semibold text-zinc-900 dark:text-white">
                            {r.nombre}
                          </span>
                          <span className="block text-xs text-zinc-500">
                            {precioDe(r) > 0
                              ? `$${precioDe(r).toLocaleString("es-MX")} /${unidadDe(r)}`
                              : "Sin precio"}
                            {" · "}
                            {incluyeDe(r).length} incluidos
                          </span>
                        </span>
                      </label>
                    );
                  })}
                </div>
                <button
                  type="button"
                  onClick={() => setShowCrearRenta(true)}
                  className="mt-2 text-xs font-semibold hover:underline"
                  style={{ color: ACCENT }}
                >
                  + Dar de alta otra renta
                </button>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={() => setSaltadosAcomodo((s) => [...s, sueltoActual.id])}
                className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Saltar
              </button>
              <button
                type="button"
                onClick={guardarAcomodo}
                disabled={saving}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving
                  ? "Guardando…"
                  : rentasElegidas.length === 0
                  ? "Ninguna, siguiente"
                  : "Guardar y siguiente"}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="p-5 overflow-y-auto space-y-4">
              <div className="rounded-lg bg-zinc-50 dark:bg-zinc-800/40 px-4 py-3">
                <div className="text-lg font-semibold text-zinc-900 dark:text-white">
                  {actual.nombre}
                </div>
                <div className="text-sm text-zinc-500">
                  {precioDe(actual) > 0
                    ? `$${precioDe(actual).toLocaleString("es-MX")}`
                    : "Sin precio"}
                  {actual.descripcion ? ` · ${actual.descripcion}` : ""}
                </div>
                {/* El catálogo que el venue ya tenía cargado trae la renta como
                    un concepto más. Se señala en vez de decidirlo solo: el
                    nombre no basta —"Renta de mantelería" no es la renta del
                    espacio— y marcarla por él dejaría servicios normales
                    convertidos en rentas sin que nadie lo pidiera. */}
                {/(^|\s)renta/i.test(actual.nombre || "") && form.tipo !== "renta" && (
                  <p className="mt-2 text-xs text-amber-600">
                    Por el nombre, esto podría ser la renta de un espacio. Si lo
                    es, márcalo abajo y dile qué trae incluido.
                  </p>
                )}
              </div>
              <ServicioForm
                key={actual.id}
                form={form}
                setForm={setForm}
                proveedores={proveedores}
                modo="clasificar"
                itemId={actual.id}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2 px-5 py-4 border-t border-zinc-200 dark:border-zinc-700/60">
              <button
                type="button"
                onClick={saltar}
                className="px-2 py-1 text-sm text-zinc-500 hover:text-zinc-700"
              >
                Saltar
              </button>
              <button
                type="button"
                onClick={() => guardar()}
                disabled={saving}
                className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm disabled:opacity-50"
                style={{ backgroundColor: ACCENT }}
              >
                {saving ? "Guardando…" : "Guardar y siguiente"}
                <ArrowRightIcon className="h-4 w-4" />
              </button>
            </div>
          </>
        )}
      </div>

      {/* Alta de la renta sin salirse del asistente: el snapshot del catálogo
          la trae enseguida y el paso 2 la ofrece de inmediato. */}
      <ServicioCreateDialog
        open={showCrearRenta}
        onClose={() => setShowCrearRenta(false)}
        inicial={{ tipo: "renta" }}
      />
    </div>
  );
};

export default ClasificacionWizard;
